import { Injectable } from '@angular/core';
import { App, Platform} from 'ionic-angular';
import { BehaviorSubject } from 'rxjs';
import {LoggerProvider, LoggerLevel, LoggerEvent} from '../logger/logger';
import { LoadingProvider } from '../loading/loading';
import { AnalyticsBankVerificationMethod, AnalyticsEventType, AnalyticsPaymentMethodData, LegacyAccountRelinkEntryPoint, LegacyAccountRelinkOutcome, SegmentLocationNames } from '../../model/types/analytics.const';
import { IBankAccount } from '../../model/models/payment-methods/bank-account.interface';
import { SegmentProvider } from '../../providers/analytics/segment';
import { PaymentProvider } from '../../providers/payment/payment';
import { HttpProvider } from '../../providers/http/http';
import { IPlaidMetaData, IPlaidUpdateResponse } from '../../model/api/payment-accounts/bank-verification.interface';
import { AuthProvider } from '../../providers/authorization';
import { CustomerProvider } from '../../providers/customer/customer';
import { RouteProvider } from '../../providers/route/route';
import { ErrorCodeType } from '../../model/types/error-code-type';
import { TranslateService } from '@ngx-translate/core';
import { RmtPopupComponent } from '../../components/rmt-popup/rmt-popup';
import { RmtSnackbarComponent } from '../../components/rmt-snackbar/rmt-snackbar';
import { BankAccountVerificationStatus } from '../../model/types/bank-account-verification-status';
import { BankAccountVerificationType } from '../../model/types/bank-account-verification-type';
import { PaymentMethodType, PaymentVerificationType } from '../../model/types/payment-method-type';
import { NewPaymentErrorType } from '../../model/types/newPayment-error-type';
import { PlaidErrorDescripton } from '../../model/types/plaid-types';
import { APP_CONFIG } from '../../environment';

export interface IPlaidRelinkOptions {
  entryPoint: LegacyAccountRelinkEntryPoint;
}

export interface IPlaidRelinkSession {
  bank: IBankAccount;
  entryPoint: LegacyAccountRelinkEntryPoint;
  outcome: Promise<LegacyAccountRelinkOutcome>;
  settled: LegacyAccountRelinkOutcome | null;
  resolve: (outcome: LegacyAccountRelinkOutcome) => void;
}

@Injectable()
export class PlaidProvider {

  private plaidPlugin;

  isLoading: boolean;
  onCreated: any;
  requestId: string;
  accountID: string;
  publicToken: string;
  plaidExited: boolean;

  plaidflowDone: BehaviorSubject<any> = new BehaviorSubject(false);

  static readonly RELINK_SHEET_IMAGE = 'assets/imgs/plaid-ria-link.svg';
  static readonly RELINK_SHEET_CSS_CLASS = 'plaid-relink-modal';

  constructor(private platform: Platform,
    private loadingProvider: LoadingProvider,
     private analytics: SegmentProvider,
     private paymentProvider: PaymentProvider,
     private httpProvider: HttpProvider,
     private routeProvider: RouteProvider,
     private translate: TranslateService,
     private customerProvider: CustomerProvider)
     { }


  public start(linkToken:String, callback: (response: any) => void, eventCallback: (event: any) => void ) {
    this.platform.ready().then(() => {
      if (this.platform.is('cordova')) {
        this.plaidPlugin = (<any>window).PlaidPlugin;

        this.plaidPlugin.listen(event=>{
          if(eventCallback !== null)
            eventCallback(event);
          else
            LoggerProvider.info(this, `Plaid event ${JSON.stringify(event)}`);
        })

        this.plaidPlugin.start({
          "PUBLIC_TOKEN":linkToken,
        }, response => {
          callback(response);
        });
    }});
  }


  async plaidNative(params: any){
    if(!this.platform.is('cordova')) {
      RmtSnackbarComponent.present('Simulating plaid onSuccessPlaid');
      // this.onSuccessPlaid("token", {metadataJson: '{"institution": {name:""}}'}, {bank: {accountId: "1234"}, onContinue: ''});
      // this.routeProvider.popToOrPush(this.routeProvider.pages.PaymentAccountListPage);
      this.resolveRelink(params, LegacyAccountRelinkOutcome.Exited);
      return;
    }
    this.onCreated = params.onCreated;
    const bank = params.bank;
    const verifyAgain = params.verifyAgain;

    const updatePlaidAcountToken = bank ? bank.bankVerificationPublicToken: null;
    this.trackNewBankAccountEvent()
    // warm the customer cache while the user is inside Plaid Link - onSuccessPlaidCreateMode
    // needs it for the billing address and pays a full fetch otherwise
    this.customerProvider.getCustomer().catch(() => {});
    let result;
    
    this.isLoading=true;
    this.loadingProvider.show();

    await this.httpProvider.refresh();

    if (verifyAgain && bank) {
      result = await this.paymentProvider.getPlaidUpdateMode(bank.accountId);
    } else {
      result = await this.paymentProvider.getPlaidSettings();
    }

    // this.setPlaidFlowDone(true);

    if(updatePlaidAcountToken !== null && updatePlaidAcountToken !== '') result.LinkToken = updatePlaidAcountToken;

    if (result.LinkToken) {
      this.loadingProvider.show();
      this.start(result.LinkToken, (response: any) => {
        LoggerProvider.log(this, LoggerLevel.Info, LoggerEvent.Firebase, `Plaid response ` + JSON.stringify(response));
        let handled: Promise<void>;
        if (response.publicToken && response.publicToken !== null && response.publicToken !== null) {

          handled = this.onSuccessPlaid(response.publicToken, response.metadata, params);

        } else {
          handled = this.onExitPlaid(response.error, response.metadata, params);
        }
        this.settleRelink(params, handled);
        this.isLoading = false;
        this.loadingProvider.hide();
      }, (event: any) => {
        // Plaid UI is on screen: release the loading overlay so its watchdog
        // doesn't fire "Action timed out" while the user completes the flow
        if (this.isLoading) {
          this.isLoading = false;
          this.loadingProvider.hide();
        }
        this.onEventPlaid(event.eventName, event.eventMetadata)
        LoggerProvider.info(this, `Plaid event ${JSON.stringify(event)}`);
      });
    } else {
      this.isLoading = false;
      this.loadingProvider.hide();
      this.resolveRelink(params, LegacyAccountRelinkOutcome.Failed);
      this.showPlaidGenericError(params);
    }

  }

  showRelinkSheet(bank: IBankAccount, options: IPlaidRelinkOptions) {
    let plaidStarted = false;
    const opened = this.routeProvider.openActionSheet(
      this.routeProvider.pages.ModalAlertPage,
      {
        image: PlaidProvider.RELINK_SHEET_IMAGE,
        imageWithCircle: false,
        customeImgStyle: { width: '140px', height: 'auto', 'max-height': 'none' },
        title: this.translate.instant('page.paymentAccountListPage.bankRelinkPlaidHeader', { bankName: bank.name }),
        subtitle: this.translate.instant('page.paymentAccountListPage.bankRelinkPlaidBody'),
        primaryButtonText: this.translate.instant('page.paymentAccountListPage.bankRelinkPlaidButton'),
        showPrimaryButton: true,
        dismissFromPrimary: false,
        primaryButtonAction: async (_rp, modalRef) => {
          plaidStarted = true;
          await modalRef.viewCtrl.dismiss();
          await this.startRelink(bank, options.entryPoint);
        },
        showSecondaryButton: true,
        secondaryButtonText: this.translate.instant('global.notNowButton')
      },
      () => {
        if (!plaidStarted)
          this.trackRelinkCompleted(bank, options.entryPoint, LegacyAccountRelinkOutcome.Dismissed);
      },
      PlaidProvider.RELINK_SHEET_CSS_CLASS
    );
    if (opened)
      this.trackRelinkPrompted(bank, options.entryPoint);
  }

  async startRelink(bank: IBankAccount, entryPoint: LegacyAccountRelinkEntryPoint): Promise<LegacyAccountRelinkOutcome> {
    const session = this.createRelinkSession(bank, entryPoint);
    const relinkParams = { relinkBank: bank, relinkSession: session };
    try {
      await this.plaidNative(relinkParams);
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Plaid relink could not be started');
      this.isLoading = false;
      this.loadingProvider.hide();
      session.resolve(LegacyAccountRelinkOutcome.Failed);
      this.showPlaidGenericError(relinkParams);
    }
    const outcome = await session.outcome;
    this.trackRelinkCompleted(bank, entryPoint, outcome);
    return outcome;
  }

  private createRelinkSession(bank: IBankAccount, entryPoint: LegacyAccountRelinkEntryPoint): IPlaidRelinkSession {
    let resolveOutcome: (outcome: LegacyAccountRelinkOutcome) => void;
    const outcome = new Promise<LegacyAccountRelinkOutcome>((res) => resolveOutcome = res);
    const session: IPlaidRelinkSession = { bank, entryPoint, outcome, settled: null, resolve: null };
    session.resolve = (result: LegacyAccountRelinkOutcome) => {
      if (session.settled === null)
        session.settled = result;
      resolveOutcome(result);
    };
    return session;
  }

  private resolveRelink(params: any, outcome: LegacyAccountRelinkOutcome) {
    const session: IPlaidRelinkSession = params && params.relinkSession;
    if (session)
      session.resolve(outcome);
  }

  private settleRelink(params: any, handled: Promise<void>) {
    const session: IPlaidRelinkSession = params && params.relinkSession;
    if (!session)
      return;
    handled.then(
      () => session.resolve(LegacyAccountRelinkOutcome.Failed),
      (ex) => {
        LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Plaid relink response could not be handled');
        session.resolve(LegacyAccountRelinkOutcome.Failed);
      }
    );
  }

  trackRelinkViewed(entryPoint: LegacyAccountRelinkEntryPoint, eligibleAccountCount: number) {
    if (eligibleAccountCount <= 0)
      return;
    this.analytics.trackEvent({ eventName: AnalyticsEventType.LegacyAccountRelinkViewed, analyticsData: {
      entryPoint,
      eligibleAccountCount
    }});
  }

  private trackRelinkPrompted(bank: IBankAccount, entryPoint: LegacyAccountRelinkEntryPoint) {
    this.analytics.trackEvent({ eventName: AnalyticsEventType.LegacyAccountRelinkPrompted, analyticsData: {
      entryPoint,
      bankAccountId: bank.accountId
    }});
  }

  private trackRelinkCompleted(bank: IBankAccount, entryPoint: LegacyAccountRelinkEntryPoint, outcome: LegacyAccountRelinkOutcome) {
    this.analytics.trackEvent({ eventName: AnalyticsEventType.LegacyAccountRelinkCompleted, analyticsData: {
      entryPoint,
      bankAccountId: bank.accountId,
      outcome
    }});
  }

  trackEvent(analyticsEvent: AnalyticsEventType, method: string, params = null) {
    this.analytics.trackEvent({ eventName: analyticsEvent, analyticsData: {
      location: (this.onCreated ? SegmentLocationNames.SendMoney : SegmentLocationNames.Settings),
      paymentVerificationMethod: method,
      ...params
    }});
  }

  async trackNewBankAccountEvent() {
    var paymentAccount, hasbankAccounts
    try {
      paymentAccount = await this.paymentProvider.getPaymentAccounts();
      hasbankAccounts = paymentAccount && paymentAccount.bankAccounts && paymentAccount.bankAccounts.length > 0 ? true : false 
    }
    catch (ex) {
      LoggerProvider.error(this, LoggerEvent.ProviderException, "Error fetching payment accounts ", ex)
    }
    this.analytics.trackEvent({ eventName: AnalyticsEventType.NewPaymentMethodStarted, analyticsData: {
      location: (this.onCreated ? SegmentLocationNames.SendMoney : SegmentLocationNames.Settings),
      paymentMethod: AnalyticsPaymentMethodData.BankAccount,
      paymentVendor: 'Plaid',
      senderCountry: APP_CONFIG.region.defaultCountryFrom,
      hasSavedPaymentMethods: hasbankAccounts
    }});
  }

  async onSuccessPlaid(public_token, metadata, params?: any) {
    const verifyAgain = params.verifyAgain;

    metadata = this.jsonParseMetaData(metadata);
    LoggerProvider.info(this, 'Plaid successful', { public_token: public_token, metadata: metadata });
    this.trackEvent(AnalyticsEventType.BankVerificationComplete, 'Plaid', {
      institutionName: (metadata && metadata.institution) ? metadata.institution.name : '',
      institutionId: (metadata && metadata.institution) ? metadata.institution.institution_id : '',
      type: (metadata && metadata.account) ? metadata.account.type : ''
    });

    // Plaid micro deposits
    if (metadata && metadata.account && metadata.account.verification_status == 'manually_verified' && params && params.bank && params.bank.accountId) {
      let payLoad = {
        paymentAccountId: params.bank.accountId,
        bankVerificationAccessToken: "",
        accountVerificationStatus: metadata.account.verification_status
      }
      this.isLoading = true;
      this.loadingProvider.show();
      try {
        const response = await this.paymentProvider.updatePlaidAccountStatus(payLoad);
        if (response && response.Result && response.Result.RetVal !== 1) {
          throw response.Result;
        } else {
          RmtSnackbarComponent.present(this.translate.instant("page.activityPage.bankVerifiedSuccessMessage"));
          await this.paymentProvider.getPaymentAccounts(true);
        }
      } catch(ex) {
        this.showPlaidError(ex, params);
      }
      this.setPlaidFlowDone(true);
      this.loadingProvider.hide();
      this.isLoading = false;
    }

    // Plaid add bank account or VerifyAgain
    else if(verifyAgain) {
      this.onSuccessPlaidUpdateMode(public_token, metadata, params);
    } else {
      await this.onSuccessPlaidCreateMode(metadata, params);
    }
  }

  async onExitPlaid(error, metadata, params){
    let mappedError, mappedMetadata;
    mappedError = this.mapErrorJson(error);
    mappedMetadata = this.jsonParseMetaData(metadata);
    LoggerProvider.info(this, 'Exit plaid', { error: mappedError, metadata: mappedMetadata });

    let exitOutcome = LegacyAccountRelinkOutcome.Exited;
    if (mappedError && mappedError.errorCode)
      exitOutcome = LegacyAccountRelinkOutcome.Failed;
    this.resolveRelink(params, exitOutcome);

    if (this.plaidExited) // sometimes it's called several times
      return;
    this.plaidExited = true;

    // this.goBack();

    if (mappedError && mappedError.errorCode && 
      (mappedError.errorCode == 'TOO_MANY_VERIFICATION_ATTEMPTS' || 
      mappedError.errorCode == 'INCORRECT_DEPOSIT_AMOUNTS' || 
      mappedError.errorCode == 'INCORRECT_DEPOSIT_VERIFICATION')) {
      let payLoad = {
        paymentAccountId: params.bank.accountId,
        bankVerificationAccessToken: "",
        accountVerificationStatus: mappedError.errorCode
      }
      this.isLoading = true;
      this.loadingProvider.show();
      await this.paymentProvider.updatePlaidAccountStatus(payLoad);
      this.isLoading = false;
      this.loadingProvider.hide();
      this.routeProvider.openActionSheet(
        this.routeProvider.pages.ModalAlertPage,
        {
          image: 'assets/imgs/yellow-warning_bg_color.svg',
          title: this.translate.instant('page.paymentAccountListPage.bankVerificationFailedTitle'),
          subtitle:this.translate.instant('page.paymentAccountListPage.bankVerificationFailedText'),
          primaryButtonText: this.translate.instant('page.paymentAccountListPage.bankVerificationDeleteButton'),
          showPrimaryButton: true,
          primaryAction: () => this.processDelete(params.bank),
          showSecondaryButton: true,
          secondaryButtonText:this.translate.instant('global.gotItButton')  ,
          showFullPage: false,
          isModal: true,
          hideHeader: true,
        }, 
        () => this.plaidExited = false,
        'plaid-error-details-modal'
      );
    }
    else {
      this.showPlaidBankAccountNotAddedError(() => this.plaidExited = false);
    }
  }

  async onSuccessPlaidCreateMode( metadata, params?: any) {
    const onContinue = params.onContinue;
    if (!AuthProvider.isLoggedIn()) {
      return;
    }
    if (!params.isPlaidUpdate) {
      this.publicToken = metadata.public_token;
      this.accountID = metadata.account.id;
      const createAccountMetaData = this.buildCreateAccountMetaData(metadata);
      this.isLoading = true;
      this.loadingProvider.show();

      const relinkBank: IBankAccount = params.relinkBank || null;
      try {
        const customer = await this.customerProvider.getCustomer();
        const plaidAccount = this.buildPlaidAccount(metadata, customer, createAccountMetaData, relinkBank);
        let addedBankAccount = await this.paymentProvider.addBankAccount(plaidAccount, true);
        this.loadingProvider.hide();
        this.isLoading = false;
        if (addedBankAccount)
          await this.handleCreatedBankAccount(addedBankAccount, metadata, createAccountMetaData, params);
        else
          this.resolveRelink(params, LegacyAccountRelinkOutcome.Failed);
      } catch (ex) {
        // this.goBack();
        this.resolveRelink(params, LegacyAccountRelinkOutcome.Failed);
        this.showPlaidError(ex, params);
        this.loadingProvider.hide();
        this.isLoading = false;
      }

    }
    else {
      if (onContinue) { // not tried yet
        onContinue();
      }
      else {
        this.paymentProvider.getPaymentAccounts(true); // reload payment accounts to include new one
        // this.routeProvider.popTo(this.routeProvider.pages.PaymentAccountListPage);
      }
    }
    this.loadingProvider.hide();
    this.isLoading = false;
  }

  private buildCreateAccountMetaData(metadata) {
    return {
      institutionId: metadata.institution.institution_id,
      institutionName: metadata.institution.name,
      accountName: metadata.account.name,
      linkSessionId: metadata.link_session_id,
      requestId: this.requestId,
      accountVerificationStatus: metadata.account.verification_status
    };
  }

  private buildPlaidAccount(metadata, customer, createAccountMetaData, relinkBank: IBankAccount) {
    return {
      replacesPaymentAccountId: relinkBank ? relinkBank.accountId : undefined,
      accountId: this.accountID,
      accountType: metadata.account.subtype,
      firstName: customer.firstName,
      middleName: customer.middleName,
      lastName: customer.lastName,
      billingAddress: customer.address.street,
      billingCity: customer.address.city,
      billingPostalCode: customer.address.postCode,
      billingState: customer.address.stateProvince,
      maskedAccountNumber: metadata.account.mask,
      bankName: metadata.institution.name,
      publicToken: this.publicToken,
      metaData: createAccountMetaData
    };
  }

  private async handleCreatedBankAccount(addedBankAccount, metadata, createAccountMetaData, params) {
    if (params.relinkBank) {
      this.resolveRelink(params, LegacyAccountRelinkOutcome.Linked);
      this.paymentProvider.notifyPossibleChanges();
    }
    if (this.onCreated && createAccountMetaData &&
      createAccountMetaData.accountVerificationStatus !== 'pending_automatic_verification' &&
      createAccountMetaData.accountVerificationStatus !== 'pending_manual_verification') {
      this.onCreated({ bank: addedBankAccount });
      return;
    }
    const returnViaBridge = params.location === 'bridge' && params.onBridgeReturn;
    // account already exists: hand the user back to RMT5 right away, its own
    // refetch runs in parallel with the native reload below. RMT5 owns the
    // loading UX from here - no native spinner on top of its loader
    if (returnViaBridge) {
      params.onBridgeReturn();
    } else {
      this.loadingProvider.show();
      this.isLoading = true;
    }
    const paymentAccounts = await this.paymentProvider.getPaymentAccounts(true); // reload payment accounts to include new one
    this.paymentProvider.setNewBank(metadata.account);
    if (!returnViaBridge) {
      this.routeProvider.popToOrPush(this.routeProvider.pages.PaymentAccountListPage, {paymentMethod: PaymentMethodType.BankAccount});
    }
    if (paymentAccounts.bankAccounts.length === 1 &&
      paymentAccounts.bankAccounts[0].verificationStatus === BankAccountVerificationStatus.WaitingOnVerificationDeposits)
      this.showBankVerificationPendingModal();
    else
      RmtSnackbarComponent.present(this.translate.instant("page.activityPage.newBankAddedText"));
    this.setPlaidFlowDone(true);
  }
  
  getBankVerificationType(bankAccount){
    if(bankAccount.bankAccountVerificationType === BankAccountVerificationType.InstantAuth)
      return AnalyticsBankVerificationMethod.Instant
    else if(bankAccount.bankAccountVerificationType === BankAccountVerificationType.AutomatedMicroDeposit)
       return AnalyticsBankVerificationMethod.AutomatedMicroDeposits
    else if(bankAccount.bankAccountVerificationType === BankAccountVerificationType.SameDayMicroDeposit)
       return AnalyticsBankVerificationMethod.SameDayMicroDeposits

  }

  async onSuccessPlaidUpdateMode(public_token, metadata, params) {
    const onContinue = params.onContinue;
    const bank = params.bank;
    try {
      this.publicToken = metadata.public_token;
      this.accountID = metadata.account.id;
      const updateAccountMetaData: IPlaidMetaData = {
        institutionId: metadata.institution.institution_id,
        institutionName: metadata.institution.name,
        accountName: metadata.account.name,
        linkSessionId: metadata.link_session_id,
        requestId: this.requestId
      }
      const response: IPlaidUpdateResponse = await this.paymentProvider.updateBankInstantVerificationStatus(bank.accountId, this.publicToken, this.accountID, updateAccountMetaData)
      if(response.statusMessage === "success") {
        LoggerProvider.info(this, 'Plaid Verification Status api success');
        if(onContinue)
          onContinue();
        else {
          this.paymentProvider.getPaymentAccounts(true); // reload payment accounts to include new one
          // this.routeProvider.popTo(this.routeProvider.pages.PaymentAccountListPage);
        }
      } else {
        this.routeProvider.popTo(this.routeProvider.pages.PaymentAccountListPage);
        LoggerProvider.info(this, 'Plaid Verification Status api failed');
        this.showPlaidBankAccountNotAddedError();
      }
  } catch (ex) {
        this.routeProvider.popTo(this.routeProvider.pages.PaymentAccountListPage);
        LoggerProvider.exception(this, ex, LoggerEvent.GeneralException, 'Failed to verify Plaid. Bank verification status api failed');

        if (!AuthProvider.isLoggedIn()) {
          this.loadingProvider.hide();
          this.routeProvider.setRoot(this.routeProvider.pages.PreLoginPage, { redirected: false });
          return;
        }

        this.showPlaidBankAccountNotAddedError();
    }
  }

  showPlaidBankAccountNotAddedError(callback = null) {
    let image = "assets/imgs/error_emoji.svg";
    let imageWithCircle = true;
    let title = this.translate.instant('page.paymentMethods.plaidErrors.verificationExitHeader');
    let description = this.translate.instant('page.paymentMethods.bank.bankAccountNotAdded.description');
    let primaryButtonText = this.translate.instant('global.okayButton');
    let primaryAction = null;
    let secondaryButtonText = null;
    let showSecondaryButton = false;
    let dismissFromPrimary = true;

    if (APP_CONFIG.region.defaultCountryFrom === 'US') {
      image = "assets/imgs/plaid-modal-info.svg";
      imageWithCircle = false;
      title = this.translate.instant('page.paymentMethods.plaidErrors.verificationExitHeader');
      description = this.translate.instant('page.paymentMethods.plaidErrors.verificationExitContent');
      primaryButtonText = this.translate.instant('global.closeButton');
      primaryAction = null;
      secondaryButtonText = null;
      showSecondaryButton = false;
      dismissFromPrimary = true;
    }

    this.routeProvider.openActionSheet(
      this.routeProvider.pages.ModalAlertPage,
      {
        image: image,
        imageWithCircle: imageWithCircle,
        title: title,
        subtitle: description,
        primaryButtonText: primaryButtonText,
        showPrimaryButton: true,
        primaryAction: primaryAction,
        showSecondaryButton: showSecondaryButton,
        secondaryButtonText: showSecondaryButton ? this.translate.instant('global.closeButton') : null,
        secondaryAction: null,
        dismissFromSecondary: true,
        dismissFromPrimary: dismissFromPrimary
        },
      callback,
      'plaid-error-details-modal'
    );
  }

  showPlaidGenericError(params) {
    let image = "assets/imgs/error_emoji.svg";
    let imageWithCircle = true;
    let title = this.translate.instant('page.paymentMethods.bank.somethingWentWrong.header')
    let description = this.translate.instant('page.paymentMethods.bank.somethingWentWrong.description');
    let primaryButtonText = this.translate.instant('global.okayButton');
    let primaryAction = null;
    let secondaryButtonText = null;
    let showSecondaryButton = false;
    let dismissFromPrimary = true;

    if (APP_CONFIG.region.defaultCountryFrom === 'US') {
      title = this.translate.instant('page.paymentMethod.plaidErrors.generalErrorHeader');
      description = this.translate.instant('page.paymentMethods.plaidErrors.generalErrorContent');
      secondaryButtonText = this.translate.instant('global.closeButton');
      dismissFromPrimary = true;
      const relinkSession: IPlaidRelinkSession = params && params.relinkSession;
      if (relinkSession && relinkSession.settled === LegacyAccountRelinkOutcome.Linked) {
        primaryButtonText = this.translate.instant('global.closeButton');
      } else {
        primaryButtonText = this.translate.instant('global.tryAgainButton');
        showSecondaryButton = true;
        primaryAction = relinkSession
          ? async () => {
            this.trackRelinkPrompted(relinkSession.bank, relinkSession.entryPoint);
            await this.startRelink(relinkSession.bank, relinkSession.entryPoint);
          }
          : () => this.plaidNative(params);
      }
    }

    this.routeProvider.openActionSheet(
      this.routeProvider.pages.ModalAlertPage,
      {
        image: image,
        imageWithCircle: imageWithCircle,
        title: title,
        subtitle: description,
        primaryButtonText: primaryButtonText,
        showPrimaryButton: true,
        primaryAction: primaryAction,
        showSecondaryButton: showSecondaryButton,
        secondaryButtonText: secondaryButtonText,
        dismissFromSecondary: true,
        dismissFromPrimary: dismissFromPrimary
      },
      null,
      'plaid-error-details-modal'
    );
  }

  showBankVerificationPendingModal(){
    this.routeProvider.openActionSheet(
      this.routeProvider.pages.ModalAlertPage,
      {
        image: "assets/imgs/pending.svg",
        title: this.translate.instant('page.bankVerificationPage.verificationPendingTitle'),
        subtitle: this.translate.instant('page.bankVerificationPage.depositsConfirmedVerificationPendingText'),
        primaryButtonText: this.translate.instant('page.bankVerificationPage.verificationPendingButton'),
        showPrimaryButton: true,
        secondaryButtonText: this.translate.instant('page.bankVerificationPage.calculatorReturnButton'),
        showSecondaryButton: this.routeProvider.hasPage(this.routeProvider.pages.CheckPricesPage),
        secondaryAction: ()=> this.routeProvider.popTo(this.routeProvider.pages.CheckPricesPage),
      },
      null,
      'bank-verification-pending-modal'
    );
  }


  // goBack() {
  //   if (this.routeProvider.hasPage(this.routeProvider.pages.PaymentAccountListPage))
  //     this.routeProvider.popTo(this.routeProvider.pages.PaymentAccountListPage);
  // }


  onEventPlaid(eventName,metadata){
    metadata = JSON.parse(metadata.metadataJson);
    LoggerProvider.log(this, LoggerLevel.Info, LoggerEvent.PlaidEvent, eventName, metadata);

    if (eventName === 'ERROR' && metadata && metadata.error_code === 'INVALID_CREDENTIALS')
      this.trackEvent(AnalyticsEventType.BankVerificationFailed, 'Plaid', {
        errorMessage: metadata.error_message,
        errorType: metadata.error_type,
        exitStatus: metadata.exit_status,
        institutionName: metadata.institution_name,
        institutionId: metadata.institution_id,
        mfaType: metadata.mfa_type,
        timestamp: metadata.timestamp
      });

    this.requestId=metadata.requestID?metadata.requestID:'';
  }
  
  // showSkipVerifySheet() {
  //   const params = { title: this.translate.instant("page.paymentAccountListPage.verifyLaterHeader"), //this.translate.instant('page.eWalletPage.merchantQRHeader'),
  //     subtitle: this.translate.instant("page.paymentAccountListPage.verifyLaterBody"),//this.translate.instant('page.eWalletPage.merchantQRContent'),
  //     primaryButtonText: this.translate.instant('page.sendFailedPage.goBackButton'),
  //     secondaryButtonText: this.translate.instant("page.paymentAccountListPage.verifyLaterButton"),//this.translate.instant(''),
  //     // secondaryAction: () => {this.onContinue()},
  //     image: "assets/imgs/plaid-modal-info.svg",
  //     showSecondaryButton : true

  //   };
  //   this.routeProvider.openActionSheet(this.routeProvider.pages.ModalAlertPage, params,null)
  // }

  getGeneratedBankAccountForPlaidSandBox(firstTowDigits:string='11'):string{
    let resultAccount =firstTowDigits;
    let dateTime = new Date();
    resultAccount += dateTime.getFullYear().toString();
    let months=(dateTime.getMonth()+1)<10?'0'+(dateTime.getMonth()+1).toString():(dateTime.getMonth()+1).toString();
    let days=(dateTime.getDate())<10?'0'+(dateTime.getDate()).toString():(dateTime.getDate()).toString()
    let hours=(dateTime.getHours())<10?'0'+(dateTime.getHours()).toString():(dateTime.getHours()).toString()
    let minutes =(dateTime.getMinutes())<10?'0'+(dateTime.getMinutes()).toString():(dateTime.getMinutes()).toString()
    let seconds=(dateTime.getSeconds())<10?'0'+(dateTime.getSeconds()).toString():(dateTime.getSeconds()).toString()
    resultAccount +=months+days+hours+minutes+seconds;
    return resultAccount;
  }

  jsonParseMetaData(metadata) {
    try {
      if (typeof metadata.metadataJson !== 'undefined')
        return JSON.parse(metadata.metadataJson);
      if (typeof metadata.metadataJSON !== 'undefined')
        return JSON.parse(metadata.metadataJSON);
    } catch (err) {
      LoggerProvider.error(this, LoggerEvent.PlaidEvent, 'jsonParseMetaData', { err: err, metadata: metadata });
    }
  }

  mapErrorJson(error){
    if (!error) return;
    if (error.errorJson) {
      try {
        const errorJson = JSON.parse(error.errorJson);
        return {
          displayMessage: errorJson.display_message,
          errorCode: errorJson.error_code,
          errorMessage: errorJson.error_message,
          errorType: errorJson.error_type
        }
      } catch(ex) {
        LoggerProvider.exception(this,ex,LoggerEvent.GeneralException, "could not parse JSON object error.errorJson:", error.errorJson);
      }

    }
    return error;
  }

  setPlaidFlowDone(plaidflowDone: boolean) {
    this.plaidflowDone.next(plaidflowDone);
  }

  showPlaidError(ex, params){
    if (ex.errorCode == ErrorCodeType.DuplicatePaymentAccount ||
      ex.errorCode == ErrorCodeType.MaximumNumberOfPaymentAccountsReached ||
      ex.RetMsg == ErrorCodeType.DuplicatePaymentAccount) {
      if (AuthProvider.isLoggedIn()) {
        let image = "assets/imgs/error_emoji.svg";
        let imageWithCircle = true;
        let title = this.translate.instant('page.errorMessage.ria_errormessage_duplicatepaymentaccount');
        let description = this.translate.instant('page.paymentMethods.bank.bankAccountAlreadyExists.description');
        let primaryButtonText = this.translate.instant('global.okayButton');
        let primaryAction = null;
        let secondaryButtonText = null;
        let showSecondaryButton = false;
        let dismissFromPrimary = true;

        if (APP_CONFIG.region.defaultCountryFrom === 'US') {
          image = "assets/imgs/plaid-modal-info.svg";
          imageWithCircle = false;
          title = this.translate.instant('page.paymentMethod.plaidErrors.generalErrorHeader');
          description = this.translate.instant('page.paymentMethods.plaidErrors.generalErrorContent');
          primaryAction = null
          primaryButtonText = this.translate.instant('global.closeButton');
          showSecondaryButton = false;
          dismissFromPrimary = true;
        }
        this.routeProvider.openActionSheet(
          this.routeProvider.pages.ModalAlertPage,
          {
            image: image,
            imageWithCircle: imageWithCircle,
            title: title,
            subtitle: description,
            primaryButtonText: primaryButtonText,
            showPrimaryButton: true,
            primaryAction: primaryAction,
            showSecondaryButton: false,
            dismissFromPrimary: dismissFromPrimary
          },
          null,
          'plaid-error-details-modal'
        );
      }
    }
    else {
      if (AuthProvider.isLoggedIn()) {
        this.showPlaidGenericError(params);
      }
    }
  }

  async processDelete(bank){
    if (bank){
     this.loadingProvider.show();
     try {
     await this.paymentProvider.deleteAccount(bank.accountId);
     RmtSnackbarComponent.present(this.translate.instant('page.paymentAccountListPage.bankVerificationDeletedMessage'));
     this.paymentProvider.setDeletedAccountNumber(bank.accountId);
      } catch (ex) {
      RmtPopupComponent.showExceptionWithLogTranslation(this, ex, 'page.paymentAccountDetailsPage.deleteFailMessage');
     }
     this.loadingProvider.hide();
    }
  }
}




// WEBPACK FOOTER //
// ./src/providers/plaid/plaid.ts